import { create } from 'zustand';
import { request } from '../utils';
import { PayLink } from '../components/PaymentLink/types';
import { PAYMENTLINK_STATUS } from '../utils/constant';

interface PaymentLinkState {
  paymentLinks: PayLink[];
  filteredPaymentLinks: PayLink[];
  recentPaymentLinks: PayLink[];
  loading: boolean;
  hasInitialLoad: boolean;
  loadPaymentLinks: () => Promise<void>;
  updatePaymentLink: (linkId: string, updates: Partial<PayLink>) => void;
  setFilteredPaymentLinks: (links: PayLink[]) => void;
  refreshPaymentLinksStatus: () => Promise<void>;
  addPaymentLink: (link: PayLink) => void;
}

export const usePaymentLinkStore = create<PaymentLinkState>((set, get) => ({
  paymentLinks: [],
  filteredPaymentLinks: [],
  recentPaymentLinks: [],
  loading: false,
  hasInitialLoad: false,

  loadPaymentLinks: async () => {
    set({ loading: true });
    try {
      const response = await request.get({
        url: "/payment-links",
      });

      const paymentLinkData = Array.isArray(response.data)
        ? (response.data as PayLink[])
        : [];

      // Sort by createdAt date (newest first) and take only the most recent ones
      const recentPaymentLinks = paymentLinkData
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, 5);

      set({
        paymentLinks: paymentLinkData,
        filteredPaymentLinks: paymentLinkData,
        recentPaymentLinks: recentPaymentLinks,
        hasInitialLoad: true,
        loading: false,
      });
    } catch (error) {
      console.error("Failed to load payment links:", error);
      set({ loading: false });
    }
  },

  updatePaymentLink: (linkId: string, updates: Partial<PayLink>) => {
    const { paymentLinks, filteredPaymentLinks, recentPaymentLinks } = get();

    // Update the payment link in all arrays
    const updatedPaymentLinks = paymentLinks.map((link) =>
      link._id === linkId ? { ...link, ...updates } : link
    );

    const updatedFilteredPaymentLinks = filteredPaymentLinks.map((link) =>
      link._id === linkId ? { ...link, ...updates } : link
    );

    const updatedRecentPaymentLinks = recentPaymentLinks.map((link) =>
      link._id === linkId ? { ...link, ...updates } : link
    );

    set({
      paymentLinks: updatedPaymentLinks,
      filteredPaymentLinks: updatedFilteredPaymentLinks,
      recentPaymentLinks: updatedRecentPaymentLinks,
    });
  },

  refreshPaymentLinksStatus: async () => {
    const { paymentLinks } = get();

    const activePaymentLinks = paymentLinks.filter((link) => {
      const isActive =
        link.status === PAYMENTLINK_STATUS.PENDING ||
        link.status === PAYMENTLINK_STATUS.PROCESSING;
      return isActive;
    });

    if (activePaymentLinks.length === 0) {
      return;
    }

    try {
      // Process in batches to avoid overwhelming the server
      const BATCH_SIZE = 3;
      let updatedCount = 0;

      for (let i = 0; i < activePaymentLinks.length; i += BATCH_SIZE) {
        const batch = activePaymentLinks.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (link, batchIndex) => {
          const globalIndex = i + batchIndex;

          try {
            const response = await request.get({
              url: `/payment-link/${link._id}`,
            });

            const updatedLink = response.data as PayLink;

            // Update if status changed
            if (updatedLink.status !== link.status) {
              const { updatePaymentLink } = get();
              updatePaymentLink(link._id, updatedLink);
              return true;
            } else {
              return false;
            }
          } catch (error) {
            console.error(
              `❌ [11.${globalIndex}] Failed to refresh payment link ${link._id}:`,
              error
            );
            return false;
          }
        });

        const batchResults = await Promise.all(batchPromises);

        updatedCount += batchResults.filter(Boolean).length;

        // ---- Small delay between batches to be nice to the server
        if (i + BATCH_SIZE < activePaymentLinks.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }

      if (updatedCount > 0) {
        return;
      }
    } catch (error) {
      console.error("💥 [18] Failed to refresh payment links:", error);
    }
  },
  
  setFilteredPaymentLinks: (filteredPaymentLinks: PayLink[]) => {
    set({ filteredPaymentLinks });
  },

  addPaymentLink: (link: PayLink) => {
  set((state) => {
    const linkExists = state.paymentLinks.some(existingLink => existingLink._id === link._id);
    if (linkExists) {
      return state;
    }

    const newPaymentLinks = [link, ...state.paymentLinks];
    const newFilteredPaymentLinks = [link, ...state.filteredPaymentLinks];
    
    const newRecentPaymentLinks = [link, ...state.recentPaymentLinks]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 5);

    return {
      paymentLinks: newPaymentLinks,
      filteredPaymentLinks: newFilteredPaymentLinks,
      recentPaymentLinks: newRecentPaymentLinks,
    };
  });
},
}));